import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../../../Services/notification.service';
import { ExpenseTypeService } from '../../../../Services/Expenses/expense-type.service';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import { HttpParams } from '@angular/common/http';

@Component({
    selector: 'app-add-expense-type',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './add-expense-type.component.html',
    styleUrls: ['./add-expense-type.component.css'],
})
export class AddExpenseTypeComponent implements OnInit {
    expenseTypeForm!: FormGroup;
    selectedFirmId!: number;
    expenseTypes: any[] = [];
    isEditMode: boolean = false;
    editingTypeId: number | null = null;

    currentPage: number = 1;
    pageSize: number = 10;
    totalPages: number = 0;
    pagedTypes: any[] = [];

    constructor(
        private fb: FormBuilder,
        private expenseTypeService: ExpenseTypeService,
        private notificationService: NotificationService,
        private firmSelectionService: FirmSelectionService
    ) { }

    ngOnInit(): void {
        this.initForm();

        this.firmSelectionService.selectedFirmName$.subscribe((firm: any) => {
            if (firm?.id) {
                this.selectedFirmId = firm.id;
                this.getExpenseTypes();
                this.expenseTypeForm.patchValue({
                    exty_firm_id: firm.id
                });
            }
        });
    }

    initForm() {
        this.expenseTypeForm = this.fb.group({
            exty_name: ['', Validators.required],
            exty_firm_id: [null, Validators.required]
        });
    }

    getExpenseTypes() {
        if (!this.selectedFirmId) return;

        let params = new HttpParams().set('firm_id', this.selectedFirmId.toString());

        this.expenseTypeService.getExpenseTypes(params).subscribe((response: any) => {
            this.expenseTypes = response.data || [];
            this.setupPagination();
        });
    }

    setupPagination(): void {
        this.totalPages = Math.ceil(this.expenseTypes.length / this.pageSize);
        this.updatePagedTypes();
    }

    updatePagedTypes(): void {
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.pagedTypes = this.expenseTypes.slice(startIndex, endIndex);
    }

    goToPage(page: number): void {
        if (page < 1 || page > this.totalPages) return;
        this.currentPage = page;
        this.updatePagedTypes();
    }

    saveExpenseType(): void {
        if (this.expenseTypeForm.invalid) {
            this.notificationService.showError('Please enter a name and ensure firm is selected.', 'Validation');
            return;
        }

        const payload = this.expenseTypeForm.value;

        if (this.isEditMode && this.editingTypeId) {
            this.expenseTypeService.updateExpenseType(this.editingTypeId, payload).subscribe({
                next: () => {
                    this.notificationService.showSuccess('Expense Type updated successfully!', 'Success');
                    this.resetForm();
                    this.getExpenseTypes();
                },
                error: (err) => {
                    this.notificationService.showError(err.error?.message || 'Failed to update.', 'Error');
                }
            });
        } else {
            this.expenseTypeService.createExpenseType(payload).subscribe({
                next: () => {
                    this.notificationService.showSuccess('Expense Type added successfully!', 'Success');
                    this.resetForm();
                    this.getExpenseTypes();
                },
                error: (err) => {
                    this.notificationService.showError(err.error?.message || 'Failed to save.', 'Error');
                }
            });
        }
    }

    editType(type: any) {
        this.isEditMode = true;
        this.editingTypeId = type.id;
        this.expenseTypeForm.patchValue({
            exty_name: type.exty_name,
            exty_firm_id: type.exty_firm_id
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    deleteType(id: number) {
        if (confirm('Are you sure you want to delete this expense type?')) {
            this.expenseTypeService.deleteExpenseType(id).subscribe({
                next: () => {
                    this.notificationService.showSuccess('Deleted successfully!', 'Success');
                    this.getExpenseTypes();
                },
                error: () => {
                    this.notificationService.showError('Failed to delete.', 'Error');
                }
            });
        }
    }

    resetForm() {
        this.isEditMode = false;
        this.editingTypeId = null;
        this.expenseTypeForm.reset({
            exty_firm_id: this.selectedFirmId
        });
    }
}
